import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Field } from '@osd/ui/field';
import { Radio } from '@osd/ui/radio';
import { RadioGroup, RadioOption } from '@osd/ui/radio-group';
import { Stack } from '@osd/ui/stack';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/radio — live docs for the `Radio` primitive.
 */
export const Route = createFileRoute('/ui/_layout/radio')({
  component: RadioPage,
});

function RadioPage() {
  return (
    <Stack gap={6}>
      <Section title="Radio" hint="mutually-exclusive choice — same name groups them">
        <Stack gap={3}>
          <Field label="Theme">
            <Radio name="theme" value="light" label="Light" defaultChecked />
            <Radio name="theme" value="dark" label="Dark" />
            <Radio name="theme" value="auto" label="Auto" />
          </Field>
          <Field label="Plan">
            <Radio name="plan" value="free" label="Free" />
            <Radio name="plan" value="pro" label="Pro — $10/mo" defaultChecked />
          </Field>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Uncontrolled group — same name"
            hint="The browser picks the checked one when the form is submitted"
            language="tsx"
            code={`<Field label="Theme">
  <Radio name="theme" value="light" label="Light" defaultChecked />
  <Radio name="theme" value="dark" label="Dark" />
  <Radio name="theme" value="auto" label="Auto" />
</Field>`}
          />

          <CodeBlock
            title="Controlled — single state"
            language="tsx"
            code={`const [theme, setTheme] = useState('light');
return (
  <Field label="Theme">
    <Radio
      name="theme"
      value="light"
      label="Light"
      checked={theme === 'light'}
      onChange={() => setTheme('light')}
    />
    <Radio
      name="theme"
      value="dark"
      label="Dark"
      checked={theme === 'dark'}
      onChange={() => setTheme('dark')}
    />
  </Field>
);`}
          />
        </Stack>
      </Section>

      <Section
        title="RadioGroup"
        hint="Semantic + accessible grouping — <fieldset> + <legend> with WAI-ARIA 'radio group' keyboard nav."
      >
        <RadioGroupStackDemo />
      </Section>

      <Section
        title="RadioGroup — inline"
        hint='Pass orientation="inline" to put options on a single row.'
      >
        <RadioGroup legend="Density" defaultValue="cozy" orientation="inline">
          <RadioOption value="compact" label="Compact" />
          <RadioOption value="cozy" label="Cozy" />
          <RadioOption value="spacious" label="Spacious" />
        </RadioGroup>
      </Section>

      <Section
        title="RadioGroup — controlled"
        hint="Pass value + onValueChange to drive the selection from outside."
      >
        <RadioGroupControlledDemo />
      </Section>

      <Section title="RadioGroup — code examples" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Bare mount (uncontrolled)"
            language="tsx"
            code={`import { RadioGroup, RadioOption } from '@osd/ui/radio-group';

<RadioGroup legend="Theme" defaultValue="auto">
  <RadioOption value="light" label="Light" />
  <RadioOption value="dark" label="Dark" />
  <RadioOption value="auto" label="Auto" />
</RadioGroup>`}
          />

          <CodeBlock
            title="Controlled + disabled group"
            language="tsx"
            code={`import { useState } from 'react';
import { RadioGroup, RadioOption } from '@osd/ui/radio-group';

const [plan, setPlan] = useState('pro');
const isReadOnly = useReadOnly();

<RadioGroup
  legend="Plan"
  legendHint="Pick the tier you want to be on"
  value={plan}
  onValueChange={setPlan}
  disabled={isReadOnly}
>
  <RadioOption value="free" label="Free" />
  <RadioOption value="pro" label="Pro" />
  <RadioOption value="team" label="Team" />
  <RadioOption value="business" label="Business" disabled />
</RadioGroup>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}

function RadioGroupStackDemo(): JSX.Element {
  return (
    <RadioGroup legend="Theme" defaultValue="auto">
      <RadioOption value="light" label="Light" hint="Light text on a dark background" />
      <RadioOption value="dark" label="Dark" hint="Dark text on a light background" />
      <RadioOption value="auto" label="Auto" hint="Follow the OS preference" />
    </RadioGroup>
  );
}

function RadioGroupControlledDemo(): JSX.Element {
  const [theme, setTheme] = useState('auto');
  return (
    <Stack gap={3}>
      <RadioGroup
        legend="Theme"
        value={theme}
        onValueChange={setTheme}
        legendHint="Controlled via useState in the parent"
      >
        <RadioOption value="light" label="Light" />
        <RadioOption value="dark" label="Dark" />
        <RadioOption value="auto" label="Auto" />
      </RadioGroup>
      <small style={{ color: 'var(--osd-color-fg-muted)' }}>
        Current selection: <code>{theme}</code>. Switch via the radios above — the value is driven
        from <code>useState</code> in the parent.
      </small>
    </Stack>
  );
}
